import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../context/AuthContext';

const AdminNavigation = ({ isCollapsed = false, onToggleCollapse }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, switchToUserView } = useAuth();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/admin-dashboard',
      icon: 'LayoutDashboard',
      description: 'Business overview and analytics'
    },
    {
      label: 'Products',
      path: '/admin/products',
      icon: 'Package',
      description: 'Manage product catalog'
    },
    {
      label: 'Orders',
      path: '/admin/orders',
      icon: 'ShoppingCart',
      description: 'Order management and fulfillment'
    },
    {
      label: 'Customers',
      path: '/admin/customers',
      icon: 'Users',
      description: 'Customer management and support'
    },
    {
      label: 'Vendors',
      path: '/admin/vendors',
      icon: 'Building2',
      description: 'Vendor relationships and inventory'
    },
    {
      label: 'Analytics',
      path: '/admin/analytics',
      icon: 'BarChart3',
      description: 'Sales and performance metrics'
    }
  ];

  const secondaryItems = [
    {
      label: 'Settings',
      path: '/admin/settings',
      icon: 'Settings',
      description: 'System configuration'
    },
    {
      label: 'Help',
      path: '/admin/help',
      icon: 'HelpCircle',
      description: 'Documentation and support'
    }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path || location?.pathname?.startsWith(path + '/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleToggleCollapse = () => {
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  const handleSwitchToUserView = () => {
    setIsProfileMenuOpen(false);
    // Use the AuthContext's switchToUserView function instead of just navigating
    if (typeof switchToUserView === 'function') {
      switchToUserView();
    } else {
      // Fallback to basic navigation if context function not available
      navigate('/user-account-dashboard');
    }
  };

  const handleAccountSettings = () => {
    setIsProfileMenuOpen(false);
    navigate('/admin/settings');
  };

  const handleLogout = () => {
    setIsProfileMenuOpen(false);
    logout();
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:flex-col bg-card border-r border-border shadow-elevation-2 transition-all duration-300 ${
        isCollapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!isCollapsed && (
            <Link to="/admin-dashboard" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Sparkles" size={20} color="white" />
              </div>
              <span className="font-heading font-semibold text-lg text-foreground" data-ocwp-field="brandName">
                Bounderlaster
              </span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleCollapse}
            className={isCollapsed ? 'mx-auto' : ''}
          >
            <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={20} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {/* Primary Navigation */}
          <div className="space-y-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-smooth group ${
                  isActivePath(item?.path)
                    ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                } ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon name={item?.icon} size={20} />
                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{item?.label}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {item?.description}
                    </div>
                  </div>
                )}
              </Link>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-border my-4"></div>

          {/* Secondary Navigation */}
          <div className="space-y-1">
            {secondaryItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                  isActivePath(item?.path)
                    ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                } ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon name={item?.icon} size={20} />
                {!isCollapsed && <span>{item?.label}</span>}
              </Link>
            ))}
          </div>
        </nav>

        {/* User Profile - Now Clickable with Dropdown */}
        <div className="p-4 border-t border-border relative">
          <button
            onClick={toggleProfileMenu}
            className={`w-full flex items-center rounded-lg transition-smooth hover:bg-muted p-2 ${
              isCollapsed ? 'justify-center' : 'space-x-3'
            }`}
          >
            <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={16} color="white" />
            </div>
            {!isCollapsed && (
              <>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium text-foreground truncate">
                    {user?.name || 'Admin User'}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {user?.email || 'admin@atelierbeauty.demo'}
                  </div>
                </div>
                <Icon 
                  name={isProfileMenuOpen ? "ChevronUp" : "ChevronDown"} 
                  size={16} 
                  className="flex-shrink-0 text-muted-foreground"
                />
              </>
            )}
          </button>

          {/* Profile Dropdown Menu */}
          {isProfileMenuOpen && !isCollapsed && (
            <div className="absolute bottom-full left-4 right-4 mb-2 bg-card border border-border rounded-lg shadow-elevation-3 overflow-hidden z-50">
              <button
                onClick={handleSwitchToUserView}
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-muted transition-smooth text-left"
              >
                <Icon name="ArrowLeftRight" size={16} className="text-primary" />
                <div>
                  <div className="text-sm font-medium text-foreground">Switch to User View</div>
                  <div className="text-xs text-muted-foreground">Access your customer account</div>
                </div>
              </button>
              
              <div className="border-t border-border"></div>
              
              <button
                onClick={handleAccountSettings}
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-muted transition-smooth text-left"
              >
                <Icon name="Settings" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">Account Settings</span>
              </button>
              
              <div className="border-t border-border"></div>
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-destructive/10 transition-smooth text-left"
              >
                <Icon name="LogOut" size={16} className="text-destructive" />
                <span className="text-sm text-destructive">Logout</span>
              </button>
            </div>
          )}

          {/* Collapsed State - Tooltip Menu */}
          {isProfileMenuOpen && isCollapsed && (
            <div className="absolute bottom-full left-full ml-2 mb-2 w-64 bg-card border border-border rounded-lg shadow-elevation-3 overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-border">
                <div className="text-sm font-medium text-foreground">
                  {user?.name || 'Admin User'}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {user?.email || 'admin@atelierbeauty.demo'}
                </div>
              </div>
              
              <button
                onClick={handleSwitchToUserView}
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-muted transition-smooth text-left"
              >
                <Icon name="ArrowLeftRight" size={16} className="text-primary" />
                <div>
                  <div className="text-sm font-medium text-foreground">Switch to User View</div>
                  <div className="text-xs text-muted-foreground">Access your customer account</div>
                </div>
              </button>
              
              <div className="border-t border-border"></div>
              
              <button
                onClick={handleAccountSettings}
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-muted transition-smooth text-left"
              >
                <Icon name="Settings" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">Account Settings</span>
              </button>
              
              <div className="border-t border-border"></div>
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-destructive/10 transition-smooth text-left"
              >
                <Icon name="LogOut" size={16} className="text-destructive" />
                <span className="text-sm text-destructive">Logout</span>
              </button>
            </div>
          )}
        </div>
      </aside>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b border-border shadow-elevation-1">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/admin-dashboard" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Sparkles" size={20} color="white" />
            </div>
            <span className="font-heading font-semibold text-lg text-foreground" data-ocwp-field="brandName">
              Bounderlaster
            </span>
          </Link>

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileMenu}
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="border-t border-border bg-background">
            <nav className="px-4 py-2 space-y-1 max-h-96 overflow-y-auto">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-smooth ${
                    isActivePath(item?.path)
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{item?.label}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {item?.description}
                    </div>
                  </div>
                </Link>
              ))}

              <div className="border-t border-border my-2"></div>

              {secondaryItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                    isActivePath(item?.path)
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default AdminNavigation;